import { useLingui } from '@lingui/react/macro'
import { PaginationState, SortingState } from '@tanstack/react-table'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import { TableComponent } from 'components/table/TableComponent'
import { getSensorColor, useScatterplotContext } from 'features/timeSeries'
import { getSharedAutoRange } from 'features/timeSeries/utils/axisRangeHelpers'
import { getAllActiveItems } from 'features/timeSeries/utils/timeSeriesUtils'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { SensorScatterplotData } from 'types'

import { SensorScatterplotTableColumns } from './SensorScatterplotTableColumns'

const TableContainer = styled.div`
  width: 100%;

  thead,
  th,
  tr,
  td {
    background: transparent !important;
  }

  /* Remove button background in the remove column */
  th:nth-child(1) button,
  td:nth-child(1) button {
    background: transparent !important;
  }

  /* Collapse the empty pagination container rendered by TableComponent */
  & > div + div {
    height: auto;
    padding: 0;
  }

  /*
   * Fixed column widths:
   * remove(1) | name(2) | description(3) | yMin(4) | yMax(5) | color(6)
   * The yMin/yMax columns are identical (and editable) in BOTH scale modes,
   * so toggling the mode never reflows the table.
   */
  table {
    table-layout: fixed;
  }

  td:nth-child(2),
  td:nth-child(3) {
    max-width: none;
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 2rem;
  }

  th:nth-child(4),
  td:nth-child(4),
  th:nth-child(5),
  td:nth-child(5) {
    width: 5rem;
  }

  th:nth-child(6),
  td:nth-child(6) {
    width: 2.75rem;
  }

  /* Wide: show description column, hide subtitle in name cell */
  @container (min-width: 40rem) {
    td:nth-child(2) span + span {
      display: none;
    }
  }

  /* Narrow: hide description column, subtitle shows in name cell */
  @container (max-width: 40rem) {
    th:nth-child(3),
    td:nth-child(3) {
      display: none;
    }
  }
`

const NARROW_BREAKPOINT = 640 // ~40rem

export const SensorScatterplotTable: React.FC = () => {
  const { t } = useLingui()

  const containerRef = useRef<HTMLDivElement>(null)
  const [isNarrow, setIsNarrow] = useState(false)

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < NARROW_BREAKPOINT)
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const {
    customSensorColors,
    selectedSensors,
    setSelectedSensors,
    handleYMinChange,
    handleYMaxChange,
    sensorRanges,
    customAxisRanges,
    handleChangeColor,
    singleScaleMode,
    derivedValues,
    isLoading,
    isInitializing,
  } = useScatterplotContext()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [deletePopupPosition, setDeletePopupPosition] = useState({
    top: 0,
    left: 0,
  })

  const handleRemoveSensor = useCallback(
    (sensorId: string) => {
      setSelectedSensors((prev) =>
        prev.filter((sensor) => sensor.id !== sensorId)
      )
    },
    [setSelectedSensors]
  )

  const handleRemoveAll = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (selectedSensors.length === 0) {
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      setDeletePopupPosition({
        top: rect.bottom + 2,
        left: rect.left + 15,
      })
      setShowConfirmDelete(true)
    },
    [selectedSensors]
  )

  const confirmRemoveAll = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()
    setSelectedSensors([])
    setShowConfirmDelete(false)
  }

  const cancelRemoveAll = () => {
    setShowConfirmDelete(false)
  }

  // Single-scale placeholder: the axis renders ONE shared auto range, so
  // empty cells advertise that shared range rather than each pen's own.
  const sharedAutoRange = useMemo(
    () =>
      singleScaleMode
        ? getSharedAutoRange(
            getAllActiveItems(selectedSensors, derivedValues),
            sensorRanges
          )
        : null,
    [singleScaleMode, selectedSensors, derivedValues, sensorRanges]
  )

  const columns = SensorScatterplotTableColumns(
    handleRemoveSensor,
    handleRemoveAll,
    handleYMinChange,
    handleYMaxChange,
    sensorRanges,
    handleChangeColor,
    isNarrow,
    singleScaleMode,
    sharedAutoRange,
    !isLoading && !isInitializing
  )

  // yMin/yMax stay undefined when there is no custom override — the cell then
  // shows the auto range (or a no-data placeholder) instead of a phantom 0.
  const data: SensorScatterplotData[] = selectedSensors.map(
    (sensor, sensorIndex) => ({
      id: sensor.id,
      name: sensor.data.name,
      description: sensor.data.description || '',
      yMin: customAxisRanges[sensor.id]?.min,
      yMax: customAxisRanges[sensor.id]?.max,
      color: customSensorColors[sensor.id] || getSensorColor(sensorIndex),
    })
  )

  return (
    <TableContainer ref={containerRef}>
      <TableComponent
        data={data}
        columns={columns}
        isLoadingData={false}
        itemDescriptiveName='Tag(s)'
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        showFilterBar={false}
        showFooter={false}
        cellPadding='0.25rem'
        cellFontSize='0.75rem'
        borderSpacing='0'
      />
      {showConfirmDelete && (
        <ConfirmDeletePopup
          entityName='sensors'
          deleteMessage={t`Remove all sensors from the chart?`}
          popupPosition={deletePopupPosition}
          cancelDelete={cancelRemoveAll}
          confirmDelete={confirmRemoveAll}
        />
      )}
    </TableContainer>
  )
}
